import toast from "react-hot-toast";
import { fileApi, folderApi } from "../services/api";

// RPAA-170: single source of truth for moving items into a folder, shared by the
// file list and the breadcrumb drop targets. Each function reports its own
// outcome via toast and returns it, so callers keep their component-local state
// (selection, folder list) without duplicating the outcome rules.

// Files move in one request. The endpoint returns only {updated: N} and never
// says WHICH paths failed, so a shortfall is reported as a count.
export const moveFilesToFolder = async (
  paths,
  targetFolderId,
  { collectionName, translate, setMovingFilePaths, onOptimisticRemoveFiles }
) => {
  if (setMovingFilePaths) setMovingFilePaths((prev) => new Set([...prev, ...paths]));
  try {
    const result = await fileApi.moveFiles(paths, targetFolderId, collectionName);
    const moved = typeof result?.updated === "number" ? result.updated : paths.length;
    if (moved === 0) {
      // Nothing moved: a failed outcome, not a partial one. Remove nothing, but
      // still call the hook with an EMPTY list — that call is also what triggers
      // the silent refresh. updated === 0 means none of these paths exist
      // server-side, so without it the stale rows stay and every retry fails
      // identically until the user reloads. An empty list removes nothing.
      if (setMovingFilePaths) setMovingFilePaths(new Set());
      if (onOptimisticRemoveFiles) onOptimisticRemoveFiles([]);
      toast.error(translate("movedNoFiles"), { duration: 6000 });
      return { ok: false, moved: 0 };
    }
    if (onOptimisticRemoveFiles) onOptimisticRemoveFiles(paths);
    if (moved < paths.length) {
      toast.error(
        translate("movedFilesPartial", {
          moved,
          total: paths.length,
          failed: paths.length - moved,
        }),
        { duration: 6000 }
      );
    } else {
      toast.success(paths.length === 1
        ? translate("movedFileSuccessfully")
        : translate("movedFilesSuccessfully", { count: paths.length }));
    }
    return { ok: true, moved };
  } catch (err) {
    if (setMovingFilePaths) setMovingFilePaths(new Set());
    toast.error(err.response?.data?.detail || translate("failedToMove"));
    return { ok: false, moved: 0 };
  }
};

// Folders move one request each, so a mid-batch failure must not discard the
// ones that already succeeded. onFoldersMoved lets a caller that renders the
// folder list prune it; the optimistic hook always runs because even an empty
// list triggers the refetch that clears rows which did move.
export const moveFoldersToFolder = async (
  rawIds,
  targetFolderId,
  { translate, setMovingFolderIds, onOptimisticRemoveFolders, onFoldersMoved }
) => {
  // Skip a folder dropped or moved into itself instead of letting the backend
  // 400 on it: the move modal only excludes the first of a multi-selection, so
  // the destination can legitimately be one of the folders being moved.
  const ids = rawIds.filter((id) => id !== targetFolderId);
  if (ids.length === 0) return { moved: [], failed: [] };
  if (setMovingFolderIds) setMovingFolderIds((prev) => new Set([...prev, ...ids]));

  const moved = [];
  const failed = [];
  const errors = [];
  for (const id of ids) {
    try {
      await folderApi.moveFolder(id, targetFolderId);
      moved.push(id);
    } catch (err) {
      failed.push(id);
      errors.push(err.response?.data?.detail || err.message);
    }
  }

  if (setMovingFolderIds) setMovingFolderIds(new Set());
  if (moved.length > 0 && onFoldersMoved) onFoldersMoved(moved);
  if (onOptimisticRemoveFolders) onOptimisticRemoveFolders(moved);

  if (failed.length === 0) {
    toast.success(moved.length === 1
      ? translate("movedFolderSuccessfully")
      : translate("movedFoldersSuccessfully", { count: moved.length }));
  } else if (moved.length > 0) {
    toast.error(
      translate("movedFoldersPartial", {
        moved: moved.length,
        total: ids.length,
        failed: failed.length,
      }),
      { duration: 6000 }
    );
  } else if (failed.length === 1) {
    // One failure: the backend's own detail says more than a generic count would.
    toast.error(errors[0] || translate("failedToMove"));
  } else {
    // Several failed with possibly differing reasons, and only one can be shown,
    // so report the outcome instead of picking one arbitrarily.
    toast.error(translate("movedNoFolders"), { duration: 6000 });
  }
  return { moved, failed };
};
